<template>
  <div id="family-event-home">
    <div class="page-header">
      <div class="title">경조사 장부</div>
      <div class="subtitle">주고받은 경조사비를 기록하고 한눈에 관리하세요</div>
    </div>

    <!-- 요약 -->
    <div class="fe-summary">
      <div class="fe-kpi received">
        <div class="k-label">받은 금액</div>
        <div class="k-value">{{ getMoneyFormat(summary.received) }}<span>원</span></div>
        <div class="k-sub">{{ summary.receivedCount }}건</div>
      </div>
      <div class="fe-kpi sent">
        <div class="k-label">보낸 금액</div>
        <div class="k-value">{{ getMoneyFormat(summary.sent) }}<span>원</span></div>
        <div class="k-sub">{{ summary.sentCount }}건</div>
      </div>
      <div class="fe-kpi net">
        <div class="k-label">순수지 (받음 − 보냄)</div>
        <div class="k-value" :class="summary.net >= 0 ? 'pos' : 'neg'">
          {{ summary.net >= 0 ? '+' : '−' }}{{ getMoneyFormat(Math.abs(summary.net)) }}<span>원</span>
        </div>
        <div class="k-sub">{{ summary.net >= 0 ? '받은 게 더 많아요' : '보낸 게 더 많아요' }}</div>
      </div>
      <div class="fe-kpi total">
        <div class="k-label">전체 기록</div>
        <div class="k-value">{{ summary.total }}<span>건</span></div>
        <div class="k-sub">경사·조사 합계</div>
      </div>
    </div>

    <!-- 툴바 -->
    <div class="fe-toolbar">
      <button class="tb-add" :class="{ open: showForm }" @click="toggleForm">
        <span class="tb-ic">{{ showForm ? '×' : '+' }}</span>
        {{ showForm ? '닫기' : '경조사 기록하기' }}
      </button>
      <div class="tb-right">
        <button class="tb-ghost" :class="{ on: showSearch }" @click="showSearch = !showSearch">검색</button>
        <button class="tb-ghost" @click="fetchList">새로고침</button>
      </div>
    </div>

    <!-- 등록/수정 폼 -->
    <div class="form-section" v-show="showForm">
      <div class="form-title">
        <span>{{ isEditMode ? "경조사 수정" : "경조사 등록" }}</span>
        <div class="form-title-right">
          <span v-if="isEditMode" class="cancel-btn" @click="cancelEdit">취소</span>
          <span v-if="!isEditMode" class="excel-toggle-btn" @click="showExcelDrop = !showExcelDrop">엑셀 일괄등록</span>
        </div>
      </div>
      <!-- 엑셀 드롭 영역 -->
      <div
        v-if="showExcelDrop"
        class="excel-drop-area"
        :class="{ dragging: isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <div class="excel-drop-content" v-if="excelData.length === 0">
          <div class="drop-text">엑셀 파일을 드래그하거나 클릭하여 업로드</div>
          <div class="drop-actions">
            <label class="drop-file-label">
              파일 선택
              <input type="file" accept=".xlsx,.xls" @change="handleExcelUpload" hidden />
            </label>
            <button class="drop-template-btn" @click="downloadTemplate">양식 다운로드</button>
          </div>
        </div>
        <div class="excel-drop-preview" v-else>
          <div class="preview-header">
            <span>미리보기 ({{ excelData.length }}건)</span>
            <div class="preview-actions">
              <button class="btn-secondary" @click="excelData = []">취소</button>
              <button class="btn-primary" @click="submitExcelData">일괄 등록</button>
            </div>
          </div>
          <div class="preview-table-wrap">
            <table class="preview-table">
              <thead>
                <tr>
                  <th>날짜</th><th>경조구분</th><th>종류</th><th>받음/보냄</th>
                  <th>대상</th><th>관계</th><th>금액</th><th>금액유형</th>
                  <th>장소</th><th>메모</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in excelData" :key="idx">
                  <td>{{ row.eventDate }}</td><td>{{ row.eventType }}</td>
                  <td>{{ row.eventKind }}</td><td>{{ row.eventRole }}</td>
                  <td>{{ row.recipient }}</td><td>{{ row.relation }}</td>
                  <td>{{ row.amount }}</td><td>{{ row.amountType }}</td>
                  <td>{{ row.location }}</td><td>{{ row.memo }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group sm">
          <label>행사 일자</label>
          <input type="date" v-model="form.eventDate" />
        </div>
        <div class="form-group sm">
          <label>경조 구분</label>
          <select v-model="form.eventType">
            <option v-for="opt in eventTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="form-group sm">
          <label>종류</label>
          <select v-model="form.eventKind">
            <option value="">선택</option>
            <option v-for="opt in eventKindOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="form-group sm">
          <label>받음/보냄</label>
          <select v-model="form.eventRole">
            <option v-for="opt in eventRoleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="form-group sm">
          <label>금액 유형</label>
          <select v-model="form.amountType">
            <option v-for="opt in amountTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group sm">
          <label>대상 이름</label>
          <input type="text" v-model="form.recipient" placeholder="이름" maxlength="64" />
        </div>
        <div class="form-group sm">
          <label>관계</label>
          <input type="text" v-model="form.relation" placeholder="친구, 직장동료 등" maxlength="32" />
        </div>
        <div class="form-group sm">
          <label>수령인</label>
          <input type="text" v-model="form.receiver" placeholder="수령인" maxlength="32" />
        </div>
        <div class="form-group sm">
          <label>금액</label>
          <input type="number" v-model.number="form.amount" placeholder="금액" min="0" />
        </div>
        <div class="form-group">
          <label>장소</label>
          <input type="text" v-model="form.location" placeholder="장소" maxlength="128" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group wide">
          <label>메모</label>
          <textarea v-model="form.memo" placeholder="메모" maxlength="128"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button v-if="isEditMode" class="btn-secondary" @click="cancelEdit">취소</button>
        <button class="btn-primary" @click="submitForm">{{ isEditMode ? "수정" : "등록" }}</button>
      </div>
    </div>

    <!-- 검색 필터 -->
    <div class="search-section" v-show="showSearch">
      <div class="search-title">검색</div>
      <div class="search-grid">
        <div class="search-group">
          <label>종류</label>
          <select v-model="search.eventKind">
            <option value="">전체</option>
            <option v-for="opt in eventKindOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="search-group">
          <label>경조 구분</label>
          <select v-model="search.eventType">
            <option value="">전체</option>
            <option v-for="opt in eventTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="search-group">
          <label>받음/보냄</label>
          <select v-model="search.eventRole">
            <option value="">전체</option>
            <option v-for="opt in eventRoleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="search-group">
          <label>대상 이름</label>
          <input type="text" v-model="search.recipient" placeholder="이름 검색" />
        </div>
        <div class="search-group">
          <label>관계</label>
          <input type="text" v-model="search.relation" placeholder="관계 검색" />
        </div>
        <div class="search-group">
          <label>메모</label>
          <input type="text" v-model="search.memo" placeholder="메모 검색" />
        </div>
      </div>
      <div class="search-actions">
        <button class="btn-secondary" @click="resetSearch">초기화</button>
        <button class="btn-primary" @click="fetchList">검색</button>
      </div>
    </div>

    <!-- 목록 -->
    <div class="list-section">
      <div class="list-header">
        <div class="list-title">
          경조사 목록
          <span class="list-count" v-if="list.length > 0">({{ list.length }}건)</span>
        </div>
      </div>

      <div class="ledger" v-if="list.length > 0">
        <div class="ledger-group" v-for="g in groupedList" :key="g.ym">
          <div class="lg-head">
            <span class="lg-month">{{ formatYm(g.ym) }}</span>
            <span class="lg-meta">
              {{ g.count }}건
              <span class="lg-net" :class="g.net >= 0 ? 'plus' : 'minus'">
                순 {{ g.net >= 0 ? '+' : '−' }}{{ getMoneyFormat(Math.abs(g.net)) }}원
              </span>
            </span>
          </div>

          <div class="fe-cards">
            <div
              class="fe-card"
              v-for="item in g.items"
              :key="item.id"
              :class="item.eventType === 'HAPPY' ? 'happy' : 'sad'"
            >
              <div class="fc-head">
                <div class="fc-icon" :class="item.eventType === 'HAPPY' ? 'happy' : 'sad'">
                  {{ item.eventType === 'HAPPY' ? '경' : '조' }}
                </div>
                <div class="fc-title">
                  <div class="fc-name">
                    {{ item.recipient }}
                    <span class="fc-rel" v-if="item.relation">· {{ item.relation }}</span>
                  </div>
                  <div class="fc-sub">
                    <span class="fc-kind">{{ getEventKindLabel(item.eventKind) }}</span>
                    <span class="fc-dot">·</span>
                    <span>{{ formatDate(item.eventDate) }}</span>
                  </div>
                </div>
                <div class="fc-amt" :class="item.eventRole === 'RECEIVED' ? 'plus' : 'minus'">
                  <span class="fc-num">{{ item.eventRole === 'RECEIVED' ? '+' : '−' }}{{ getMoneyFormat(item.amount) }}</span>
                  <span class="fc-atype">{{ getEventRoleLabel(item.eventRole) }} · {{ getAmountTypeLabel(item.amountType) }}</span>
                </div>
              </div>

              <div class="fc-detail" v-if="item.location || item.receiver || item.memo">
                <div class="fc-tags" v-if="item.location || item.receiver">
                  <span class="fc-tag" v-if="item.location">📍 {{ item.location }}</span>
                  <span class="fc-tag" v-if="item.receiver">수령 {{ item.receiver }}</span>
                </div>
                <div class="fc-memo" v-if="item.memo" :class="{ full: expandedId === item.id }">
                  {{ item.memo }}
                </div>
                <button
                  class="fc-more"
                  v-if="item.memo && item.memo.length > 60"
                  @click="toggleExpand(item.id)"
                >{{ expandedId === item.id ? '접기' : '더보기' }}</button>
              </div>

              <div class="fc-actions">
                <button @click="editEvent(item)">수정</button>
                <button class="del" @click="deleteEvent(item.id)">삭제</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-message">등록된 경조사가 없습니다.</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import * as XLSX from "xlsx";
import {
  FamilyEvent,
  FamilyEventRequest,
  EVENT_TYPE_OPTIONS,
  EVENT_KIND_OPTIONS,
  EVENT_ROLE_OPTIONS,
  AMOUNT_TYPE_OPTIONS,
  getEventKindLabel,
  getEventTypeLabel,
  getEventRoleLabel,
  getAmountTypeLabel,
  getToday,
  LABEL_TO_EVENT_TYPE,
  LABEL_TO_EVENT_KIND,
  LABEL_TO_EVENT_ROLE,
  LABEL_TO_AMOUNT_TYPE,
} from "../class";
import * as familyEventApi from "../api";

export default defineComponent({
  name: "FamilyEventHomeView",
  mixins: [UtilMixin],

  setup() {
    const list = ref<FamilyEvent[]>([]);
    const form = ref(new FamilyEventRequest());
    const editingId = ref<number | null>(null);
    const isEditMode = computed(() => editingId.value !== null);
    const excelData = ref<any[]>([]);
    const showExcelDrop = ref(false);
    const isDragging = ref(false);
    const showForm = ref(false);
    const showSearch = ref(false);

    const summary = computed(() => {
      let received = 0;
      let sent = 0;
      let receivedCount = 0;
      let sentCount = 0;
      list.value.forEach((it) => {
        const amt = it.amount || 0;
        if (it.eventRole === "RECEIVED") {
          received += amt;
          receivedCount++;
        } else {
          sent += amt;
          sentCount++;
        }
      });
      return {
        received,
        sent,
        net: received - sent,
        receivedCount,
        sentCount,
        total: list.value.length,
      };
    });

    const groupedList = computed(() => {
      const sorted = [...list.value].sort((a, b) => dateNum(b.eventDate) - dateNum(a.eventDate));
      const groups: any[] = [];
      const map = new Map<string, any>();
      sorted.forEach((it) => {
        const ym = ymKey(it.eventDate);
        if (!map.has(ym)) {
          const g = { ym, items: [] as FamilyEvent[], net: 0, count: 0 };
          map.set(ym, g);
          groups.push(g);
        }
        const g = map.get(ym);
        g.items.push(it);
        g.count++;
        g.net += (it.eventRole === "RECEIVED" ? 1 : -1) * (it.amount || 0);
      });
      return groups;
    });

    const formatYm = (ym: string): string => {
      const parts = ym.split("-");
      if (parts.length < 2) return ym;
      return `${parts[0]}년 ${parseInt(parts[1], 10)}월`;
    };

    const toggleForm = () => {
      if (showForm.value && editingId.value !== null) {
        // 수정 중 닫기 → 수정 취소
        editingId.value = null;
        form.value.reset();
      }
      showForm.value = !showForm.value;
    };

    const search = reactive({
      eventType: "",
      eventKind: "",
      eventRole: "",
      recipient: "",
      relation: "",
      memo: "",
    });

    const expandedId = ref<number | null>(null);

    const toggleExpand = (id: number) => {
      expandedId.value = expandedId.value === id ? null : id;
    };

    // eventDate 는 [YYYY,M,D] 배열 또는 "YYYY-MM-DD" 문자열로 올 수 있음
    const parseYmd = (v: any): { y: number; m: number; d: number } | null => {
      if (!v) return null;
      let parts: any[];
      if (Array.isArray(v)) parts = v;
      else parts = String(v).split(/[-,./]/).map((s) => s.trim()).filter(Boolean);
      if (parts.length < 3) return null;
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const d = parseInt(parts[2], 10);
      if (!y || !m || !d) return null;
      return { y, m, d };
    };

    const formatDate = (dateStr: any): string => {
      const p = parseYmd(dateStr);
      if (!p) return "";
      return `${p.y}.${String(p.m).padStart(2, "0")}.${String(p.d).padStart(2, "0")}`;
    };

    const dateNum = (v: any): number => {
      const p = parseYmd(v);
      return p ? p.y * 10000 + p.m * 100 + p.d : 0;
    };

    const ymKey = (v: any): string => {
      const p = parseYmd(v);
      return p ? `${p.y}-${String(p.m).padStart(2, "0")}` : "미지정";
    };

    const buildSearchParams = () => {
      const params: any = {};
      if (search.eventType) params.eventType = search.eventType;
      if (search.eventKind) params.eventKind = search.eventKind;
      if (search.eventRole) params.eventRole = search.eventRole;
      if (search.recipient) params.recipient = search.recipient;
      if (search.relation) params.relation = search.relation;
      if (search.memo) params.memo = search.memo;
      return Object.keys(params).length > 0 ? params : null;
    };

    const fetchList = async () => {
      const res = await familyEventApi.getFamilyEventList(buildSearchParams());
      if (Array.isArray(res)) {
        list.value = res;
      } else {
        list.value = [];
      }
    };

    const resetSearch = () => {
      search.eventType = "";
      search.eventKind = "";
      search.eventRole = "";
      search.recipient = "";
      search.relation = "";
      search.memo = "";
      fetchList();
    };

    const submitForm = async () => {
      if (!form.value.eventType) {
        alert("경조 구분을 선택해주세요.");
        return;
      }
      if (form.value.amount === null || form.value.amount < 0) {
        alert("금액을 입력해주세요.");
        return;
      }
      if (isEditMode.value) {
        const data = await familyEventApi.updateFamilyEvent(editingId.value!, form.value);
        if (data) {
          alert("수정되었습니다.");
          cancelEdit();
          await fetchList();
        }
      } else {
        const data = await familyEventApi.createFamilyEvent(form.value);
        if (data) {
          alert("등록되었습니다.");
          form.value.reset();
          await fetchList();
        }
      }
    };

    const editEvent = (item: FamilyEvent) => {
      editingId.value = item.id;
      form.value.bindFromEvent(item);
      showForm.value = true;
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const deleteEvent = async (id: number) => {
      if (!confirm("삭제하시겠습니까?")) return;
      const data = await familyEventApi.deleteFamilyEvent(id);
      if (data) {
        alert("삭제되었습니다.");
        await fetchList();
      }
    };

    const cancelEdit = () => {
      editingId.value = null;
      form.value.reset();
      showForm.value = false;
    };

    // --- 엑셀 ---
    const downloadTemplate = () => {
      const headers = [
        "행사일자", "경조구분(경사/조사)", "종류(결혼식/생일/돌잔치/장례식/기념일/환갑/칠순/집들이/기타)",
        "받음보냄(받음/보냄)", "대상이름", "관계", "수령인", "금액", "금액유형(현금/선물)", "장소", "메모",
      ];
      const example = [
        "2026-01-15", "경사", "결혼식", "보냄", "홍길동", "친구", "본인", "50000", "현금", "강남 XX웨딩홀", "대학동기",
      ];
      const ws = XLSX.utils.aoa_to_sheet([headers, example]);
      ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length * 2, 12) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "경조사");
      XLSX.writeFile(wb, "경조사_업로드양식.xlsx");
    };

    const parseExcelDate = (val: any): string => {
      if (!val) return "";
      if (typeof val === "number") {
        const d = XLSX.SSF.parse_date_code(val);
        if (d) {
          return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
        }
      }
      return String(val);
    };

    const parseExcelFile = (file: File) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

        const parsed: any[] = [];
        for (let i = 1; i < rows.length; i++) {
          const r = rows[i];
          if (!r || r.length === 0 || !r[0]) continue;
          parsed.push({
            eventDate: parseExcelDate(r[0]),
            eventType: LABEL_TO_EVENT_TYPE[String(r[1] || "").trim()] || "HAPPY",
            eventKind: LABEL_TO_EVENT_KIND[String(r[2] || "").trim()] || "",
            eventRole: LABEL_TO_EVENT_ROLE[String(r[3] || "").trim()] || "SENT",
            recipient: String(r[4] || ""),
            relation: String(r[5] || ""),
            receiver: String(r[6] || ""),
            amount: Number(r[7]) || 0,
            amountType: LABEL_TO_AMOUNT_TYPE[String(r[8] || "").trim()] || "CASH",
            location: String(r[9] || ""),
            memo: String(r[10] || ""),
          });
        }
        excelData.value = parsed;
      };
      reader.readAsArrayBuffer(file);
    };

    const handleExcelUpload = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;
      parseExcelFile(file);
      target.value = "";
    };

    const handleDrop = (e: DragEvent) => {
      isDragging.value = false;
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      if (!file.name.match(/\.xlsx?$/i)) {
        alert("엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.");
        return;
      }
      parseExcelFile(file);
    };

    const submitExcelData = async () => {
      if (excelData.value.length === 0) return;
      if (!confirm(`${excelData.value.length}건을 일괄 등록하시겠습니까?`)) return;

      let success = 0;
      let fail = 0;
      for (const row of excelData.value) {
        try {
          const req = new FamilyEventRequest();
          req.eventDate = row.eventDate;
          req.eventType = row.eventType;
          req.eventKind = row.eventKind;
          req.eventRole = row.eventRole;
          req.recipient = row.recipient;
          req.relation = row.relation;
          req.receiver = row.receiver;
          req.amount = row.amount;
          req.amountType = row.amountType;
          req.location = row.location;
          req.memo = row.memo;
          await familyEventApi.createFamilyEvent(req);
          success++;
        } catch {
          fail++;
        }
      }
      alert(`등록 완료: ${success}건 성공${fail > 0 ? `, ${fail}건 실패` : ""}`);
      excelData.value = [];
      await fetchList();
    };

    onMounted(() => {
      fetchList();
    });

    return {
      list,
      form,
      search,
      editingId,
      isEditMode,
      excelData,
      showExcelDrop,
      isDragging,
      showForm,
      showSearch,
      summary,
      groupedList,
      formatYm,
      toggleForm,
      expandedId,
      toggleExpand,
      formatDate,
      fetchList,
      resetSearch,
      submitForm,
      editEvent,
      deleteEvent,
      cancelEdit,
      downloadTemplate,
      handleExcelUpload,
      handleDrop,
      submitExcelData,
      eventTypeOptions: EVENT_TYPE_OPTIONS,
      eventKindOptions: EVENT_KIND_OPTIONS,
      eventRoleOptions: EVENT_ROLE_OPTIONS,
      amountTypeOptions: AMOUNT_TYPE_OPTIONS,
      getEventKindLabel,
      getEventTypeLabel,
      getEventRoleLabel,
      getAmountTypeLabel,
    };
  },
});
</script>

<style lang="scss">
@import "../assets/css/index.scss";
</style>
